import type { LogEventFilter } from '@/types/data/catalog-log-event';

/** Matches backend `maxLogsConnectorMergeCount` for GET .../logs. */
export const MAX_CATALOG_LOG_CONNECTOR_IDS = 10;

/** Matches backend `maxCatalogLogSpacesCount` for GET .../logs `space` param. */
export const MAX_CATALOG_LOG_SPACE_TOKENS = 32;

/** Matches backend `maxCatalogLogSpacesParamLen` for GET .../logs `space` param. */
export const MAX_CATALOG_LOG_SPACE_PARAM_LEN = 512;

/** Backend catalog logs API max page size. */
export const MAX_CATALOG_LOG_PAGE_LIMIT = 200;

export type ListCatalogLogsPage = {
  limit?: number;
  next?: string | null;
};

/** Query object for `catalogv1.listActivityLogs` (omit unsupported keys). */
export type ListCatalogLogsApiQuery = {
  connector_ids?: string;
  space?: string;
  from?: string;
  to?: string;
  kinds?: string;
  target_ref?: string;
  distinct_id?: string;
  limit?: number;
  next?: string;
};

/** Truncates to API merge limit; callers may toast when truncation occurs. */
export function capConnectorIdsForCatalogLogs(ids: string[]): string[] {
  if (ids.length <= MAX_CATALOG_LOG_CONNECTOR_IDS) {
    return ids;
  }
  return ids.slice(0, MAX_CATALOG_LOG_CONNECTOR_IDS);
}

/**
 * Builds the `space` query value: comma-separated, deduplicated, sorted for stable
 * URLs and React Query keys; capped to {@link MAX_CATALOG_LOG_SPACE_TOKENS} and
 * {@link MAX_CATALOG_LOG_SPACE_PARAM_LEN} to match the catalog logs API.
 */
export function buildCatalogLogSpaceQueryParam(
  spaces: string[]
): string | undefined {
  const seen = new Set<string>();
  const tokens: string[] = [];
  for (const raw of spaces) {
    const t = raw.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    tokens.push(t);
    if (tokens.length >= MAX_CATALOG_LOG_SPACE_TOKENS) {
      break;
    }
  }
  if (!tokens.length) {
    return undefined;
  }
  tokens.sort();
  let csv = tokens.join(',');
  if (csv.length > MAX_CATALOG_LOG_SPACE_PARAM_LEN) {
    while (
      tokens.length > 1 &&
      tokens.join(',').length > MAX_CATALOG_LOG_SPACE_PARAM_LEN
    ) {
      tokens.pop();
    }
    csv = tokens.join(',');
    if (csv.length > MAX_CATALOG_LOG_SPACE_PARAM_LEN) {
      csv = csv.slice(0, MAX_CATALOG_LOG_SPACE_PARAM_LEN);
    }
  }
  return csv || undefined;
}

/**
 * Builds query params for `GET /catalog/v1/tenants/{tenant_id}/logs`.
 * - Sends `from`/`to` only when both are non-empty after trim (server requires the pair).
 * - Omits `connector_ids` when empty (tenant-wide feed).
 * - Clamps `limit` to {@link MAX_CATALOG_LOG_PAGE_LIMIT}.
 * - `kinds` from filter.discriminators (comma-separated) or explicit `options.kinds`.
 * - `space`: splits `filter.space` on commas and normalizes via {@link buildCatalogLogSpaceQueryParam}
 *   (OR list for `MeshEvent.GetSpace`; callers may set `filter.space` from {@link buildCatalogLogSpaceQueryParam}(spaces)).
 */
export function buildListCatalogLogsQuery(
  page?: ListCatalogLogsPage,
  filter?: LogEventFilter,
  options?: {
    connectorIds?: string[];
    targetRef?: string;
    distinctId?: string;
    kinds?: string;
  }
): ListCatalogLogsApiQuery {
  const out: ListCatalogLogsApiQuery = {};

  if (page?.limit !== undefined && page.limit > 0) {
    out.limit = Math.min(page.limit, MAX_CATALOG_LOG_PAGE_LIMIT);
  }
  if (page?.next) {
    out.next = page.next;
  }

  const from = filter?.from?.trim() ?? '';
  const to = filter?.to?.trim() ?? '';
  if (from !== '' && to !== '') {
    out.from = from;
    out.to = to;
  }

  const spaceRaw = filter?.space?.trim();
  if (spaceRaw) {
    out.space = buildCatalogLogSpaceQueryParam(spaceRaw.split(','));
  }

  const explicitIds = options?.connectorIds ?? filter?.connectorIds;
  if (explicitIds?.length) {
    out.connector_ids = explicitIds.join(',');
  }

  if (options?.kinds?.trim()) {
    out.kinds = options.kinds.trim();
  } else if (filter?.discriminators?.length) {
    out.kinds = filter.discriminators.join(',');
  }

  const targetRef = options?.targetRef?.trim();
  if (targetRef) {
    out.target_ref = targetRef;
  }

  const distinctId = options?.distinctId?.trim();
  if (distinctId) {
    out.distinct_id = distinctId;
  }

  return out;
}
